import { HttpErrorResponse } from '@angular/common/http';
import { Dataset, PublishDatasetSocketSuccessPayload } from './dataset-types';
import {
  DatasetResult,
  SwitchView,
  SwitchViewDomain,
  DatasetDetails,
  ExportedDatasetSourceContent,
  ProducerType,
  ThoughtspotPublication,
  ThoughtspotDatasetPublicationResponse,
  SpaceResource,
} from '@mode/shared/contract-common';
import { createAction, props } from '@ngrx/store';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SocketSubscription } from '@mode/shared/data-access-socket';

export const loadDatasetViewRoute = createAction('[Dataset View Page] LOAD_DATASET', props<{ datasetToken: string }>());
export const loadDatasetEditRoute = createAction('[Dataset Edit Page] LOAD_DATASET', props<{ datasetToken: string }>());
export const loadDatasetReportEditRoute = createAction(
  '[Report Edit Page] LOAD_DATASET',
  props<{ datasetToken: string }>()
);
export const loadNotebookCellDatasetEditRoute = createAction(
  '[Report Edit Page] LOAD_NOTEBOOK_CELL_DATASET',
  props<{ datasetToken: string; switchViewToken: string }>()
);

export const loadDatasetReportEditRouteSuccess = createAction(
  '[Report Edit Page] LOAD_DATASET success',
  props<{ dataset: Dataset }>()
);

export const loadDatasetSuccess = createAction(
  '[Dataset View Page] LOAD_DATASET_SUCCESS',
  props<{ datasetResult: DatasetResult; switchView: SwitchView; token: string; details?: DatasetDetails }>()
);

export const loadImportedDataset = createAction(
  '[Report Edit Page] LOAD_IMPORTED_DATASET',
  props<{ switchViewToken: string }>()
);

export const loadDatasetFailure = createAction(
  '[Dataset View Page] LOAD_DATASET_FAILURE',
  props<{ error: HttpErrorResponse }>()
);

export const deleteDataset = createAction('[Dataset Edit Page] DELETE_DATASET', props<{ datasetToken: string }>());
export const deleteDatasetSuccess = createAction('[Dataset Edit Page] DELETE_DATASET_SUCCESS');
export const deleteDatasetFail = createAction('[Dataset Edit Page] DELETE_DATASET_FAIL');

export const removePhantomDatasetClicked = createAction(
  '[Report Edit Page] REMOVE_PHANTOM_DATASET_FROM_REPORT',
  props<{ accountId: string; reportId: string; switchViewToken: string }>()
);

export const googleSheetSyncCompleted = createAction(
  '[Dataset Edit Page] GOOGLE_SHEET_SYNC_COMPLETED',
  props<{ dataView: SwitchViewDomain.DataView }>()
);

export const runDatasetPollingCompleted = createAction(
  '[Dataset Edit Page] RUN_DATASET_POLLING_COMPLETED',
  props<{ datasetToken: string }>()
);

export const openDeleteDatasetModal = createAction(
  '[Dataset Edit Page] OPEN_DELETE_DATASET_MODAL',
  props<{ token: string }>()
);

export const openDeleteDatasetModalFromCollectionList = createAction(
  '[Collection List] OPEN_DELETE_DATASET_MODAL',
  props<{ token: string }>()
);

export const openDeleteDatasetModalFromCollectionTile = createAction(
  '[Collection Tile] OPEN_DELETE_DATASET_MODAL',
  props<{ token: string }>()
);

export const openDeleteDatasetModalFromCollectionSidePanel = createAction(
  '[Collection Side Panel] OPEN_DELETE_DATASET_MODAL',
  props<{ token: string }>()
);

export const deleteFromCollection = createAction(
  '[Collection] DELETE_DATASET_FROM_COLLECTION',
  props<{ token: string }>()
);

export const deleteFromCollectionView = createAction(
  '[Collection] DELETE_DATASET_FROM_COLLECTION_VIEW',
  props<{ token: string }>()
);

export const openPublishModal = createAction(
  '[Publish Dataset] Open Publish Dataset Modal',
  props<{ switchViewToken: string }>()
);

export const openUnpublishModal = createAction(
  '[Unpublish Dataset] Open Unpublish Dataset Modal',
  props<{ switchViewToken: string }>()
);

export const openRepublishModal = createAction(
  '[Republish Dataset] Open Republish Dataset Modal',
  props<{ switchViewToken: string; queryToken?: string; selectedText?: string }>()
);

export const openPromoteModal = createAction(
  '[Promote Dataset] Open Promote Dataset Modal',
  props<{ switchViewToken: string }>()
);

export const openMoveSpaceModal = createAction(
  '[Move Space] Open Move Space Modal',
  props<{ report: any; callback?: () => void }>()
);

export const promoteQueryToDataset = createAction(
  '[Promote Dataset] Promote Query to Dataset',
  props<{ queryToken: string; reportID: string; datasetName: string; shouldAutoPublish: boolean }>()
);

export const promoteQueryToDatasetSuccess = createAction(
  '[Promote Dataset] Promote Query to Dataset Success',
  props<{ hrefLink: string }>()
);

export const promoteQueryToDatasetFailure = createAction(
  '[Promote Dataset] Promote Query to Dataset Failure',
  props<{ error: HttpErrorResponse }>()
);

export const publishDataset = createAction(
  '[Publish Dataset] Publish Dataset',
  props<{
    datasetToken: string;
    name: string;
    producerType: ProducerType;
    queryToken?: string;
    switchViewToken?: string;
  }>()
);

/**
 * This is called when publishUpdates feature flag is not enabled start
 **/

export const publishDatasetSuccess = createAction(
  '[Publish Dataset] Publish Dataset Success',
  props<{
    name: string;
    datasetToken: string;
    producerType: ProducerType;
    queryToken?: string;
    switchViewToken?: string;
    datasetPublicationResponse: ThoughtspotDatasetPublicationResponse;
  }>()
);

export const publishDatasetFailure = createAction(
  '[Publish Dataset] Publish Dataset Failure',
  props<{ error: HttpErrorResponse }>()
);

/**
 * This is called when publishUpdates flag is not enabled end
 **/

/**
 * This is called when publishUpdates feature flag is enabled start
 **/

export const initPublishDatasetSuccess = createAction(
  '[Publish Dataset] Init Publish Dataset Success',
  props<{
    name: string;
    datasetToken: string;
    producerType: ProducerType;
    switchViewToken: string;
    queryToken?: string;
    datasetPublicationResponse: ThoughtspotPublication;
  }>()
);

export const initPublishDatasetFailure = createAction(
  '[Publish Dataset] Init Publish Dataset Failure',
  props<{ switchViewToken: string; datasetToken: string; error: HttpErrorResponse }>()
);

export const publishDatasetTsInProgress = createAction(
  '[Publish Dataset] TS In Progress',
  props<{
    switchViewToken: string | undefined;
    datasetThoughtspotPublicationToken: string | undefined;
    progressToastId: number;
  }>()
);

export const publishDatasetTsPending = createAction(
  '[Publish Dataset] TS Pending',
  props<{ switchViewToken?: string }>()
);

export const publishDatasetTsSuccess = createAction(
  '[Publish Dataset] TS Success',
  props<{
    publishedUrl: string;
    switchViewToken: string;
    datasetPublicationResponse: ThoughtspotPublication;
    isAlreadyPublished?: boolean;
    producerType?: ProducerType;
    queryToken?: string;
    datasetToken?: string;
  }>()
);

export const publishDatasetTsFailed = createAction(
  '[Publish Dataset] TS Failed',
  props<{ switchViewToken: string; isAlreadyPublished?: boolean }>()
);

export const publishDatasetSocketConnectionSuccess = createAction(
  '[Publish Dataset] Socket Connection Success',
  props<{
    publishDatasetSubscription: SocketSubscription<PublishDatasetSocketSuccessPayload>;
    datasetPublicationResponse: ThoughtspotPublication;
    switchViewToken: string;
    producerType?: ProducerType;
    queryToken?: string;
    datasetToken?: string;
  }>()
);

/**
 * This is called when publishUpdates flag is enabled end
 **/

export const republishDataset = createAction(
  '[Republish Dataset] Republish Dataset',
  props<{ datasetThoughtspotPublicationToken: string; switchViewToken: string }>()
);

export const executeReportRun = createAction(
  'ExecuteReportRun',
  props<{
    runningQueryToken: string | undefined;
    selectedText: string | undefined;
    datasetThoughtspotPublicationToken: string;
  }>()
);

export const republishDatasetSuccess = createAction('[Republish Dataset] Republish Dataset Success');

export const republishDatasetFailure = createAction(
  '[Republish Dataset] Republish Dataset Failure',
  props<{ error: HttpErrorResponse }>()
);

export const initiateSocketConnection = createAction(
  '[Republish Dataset] Initiate Socket Connection',
  props<{
    switchViewToken: string;
    datasetPublicationResponse: ThoughtspotPublication;
    datasetToken?: string;
    producerType?: ProducerType;
    queryToken?: string;
  }>()
);

export const unpublishDataset = createAction(
  '[Unpublish Dataset] Unpublish Dataset',
  props<{
    datasetToken: string;
    producerType: ProducerType;
    switchViewToken: string;
    datasetThoughtspotPublicationToken: string;
  }>()
);

export const unpublishDatasetSuccess = createAction(
  '[Unpublish Dataset] Unpublish Dataset Success',
  props<{ switchViewToken: string }>()
);

export const unpublishDatasetFailure = createAction(
  '[Unpublish Dataset] Unpublish Dataset Failure',
  props<{ error: HttpErrorResponse }>()
);

export const fetchExportedDatasetSource = createAction(
  '[Exported Dataset] Exported Dataset Source',
  props<{ queryToken: string }>()
);

export const fetchExportedDatasetSourceSuccess = createAction(
  '[Exported Dataset] Exported Dataset Source Success',
  props<{ response: ExportedDatasetSourceContent }>()
);

export const fetchExportedDatasetSourceFailure = createAction(
  '[Exported Dataset] Exported Dataset Source Failure',
  props<{ error?: HttpErrorResponse }>()
);

export const publishDatasetAfterMoveSpace = createAction(
  'PublishDataset After MoveSpace',
  props<{ switchViewToken: string }>()
);

export const updateSelectedReportSpace = createAction(
  'UpdateSelectedReportSpace',
  props<{ payload: { space: SpaceResource; _cb?: () => void } }>()
);

export const openPromoteDatasetAfterMoveSpace = createAction(
  'Open Promote Dataset After MoveSpace',
  props<{ switchViewToken: string }>()
);

// same action triggered from report.data.service.ts
export const syncTsPublishMetaDataOnMoveSpace = createAction(
  'UpdateSelectedReportSpaceSuccess',
  props<{
    payload: {
      report: {
        _embedded: {
          space: {
            schema_name: string;
          };
        };
      };
    };
  }>()
);

// same action triggered from report.data.service.ts
export const syncTsPublishMetaDataOnReusableDatasetRename = createAction(
  'UpdateReportSuccess',
  props<{
    payload: {
      report: {
        _embedded: {
          ts_publish_metadata: {
            schema_name: string;
            table_name: string;
          };
        };
      };
    };
  }>()
);

// same action triggered from query.data.service.ts
export const syncTsPublishMetaDataOnRenameNotebookCell = createAction(
  'UpdateQuerySuccess',
  props<{
    payload: {
      token: string;
      _embedded: {
        ts_publish_metadata: {
          schema_name: string;
          table_name: string;
        };
      };
    };
  }>()
);

export const syncTsPublishMetaDataForLocalDataset = createAction(
  'update TsPublishMetaData Local Dataset',
  props<{
    switchViewToken: string;
    schemaName?: string;
    tableName?: string;
  }>()
);
